import { useState, useRef } from 'react';
import { useStore } from '../store/StoreContext.jsx';
import { PREPROD } from '../env.js';
import { getDashboardMetrics } from '../store/selectors.js';
import { planSections } from '../schema/planSections.js';
import PlanField from '../components/PlanField.jsx';
import PlanSummaryPanel from '../components/PlanSummaryPanel.jsx';
import EmoneyGate from '../components/EmoneyGate.jsx';
import { getMissingEmoneyFields, getEmoneyRequirements } from '../store/emoneyRequired.js';
import { getMissingSubmitFields, conditionApplies } from '../store/planSubmit.js';
import { resolveRetirement } from '../store/planRetirement.js';
import { isDateOutOfRange, getDateRange, getDateRangeHint, classifySaveError, SEND_MSG_VALIDATION, SEND_MSG_SYSTEM } from '../utils/validation.js';

function getSmartPrefills(data, m) {
  let cashFlow = '';
  if (m.totalIncome > 0) {
    if (m.cashFlow > 50) cashFlow = 'Surplus';
    else if (m.cashFlow < -50) cashFlow = 'Deficit';
    else cashFlow = 'Break-Even';
  }
  // Retirement is not prefilled here. It is an age-or-date PAIR, resolved in
  // valueFor through store/planRetirement.js, so the screen and the payload
  // read one rule instead of two that can drift.
  return {
    currentCashFlow: cashFlow,
  };
}

// TEMPORARY, for the wave-2 cutover: the "Needed for eMoney" panel and its
// block on sending are switched off. The gate stopped advisors sending a Plan
// Order at all until every eMoney dependency was filled, which is the wrong
// trade while planning still wants the form through. Flip this back to true to
// restore it — EmoneyGate and store/emoneyRequired.js are deliberately left
// intact so nothing needs rebuilding.
const EMONEY_GATE_ENABLED = false;

// What Submit demands, and every exemption from it, lives in store/planSubmit.js
// so this screen and its tests read one definition rather than two copies.
const EMPTY_KEYS = new Set();

// The age-or-date retirement questions, found by their date key or their age box.
const RETIREMENT_PAIRS = planSections.flatMap(s => s.fields).filter(f => f.type === 'retirement');
const retirementPairFor = key => RETIREMENT_PAIRS.find(f => f.key === key || f.ageKey === key);

const ARCHIVE_ENABLED = PREPROD;

export default function PlanScreen() {
  const { data, update, sendPlanOrder, saveNow, readOnly, archivePq, planningMode } = useStore();
  const m = getDashboardMetrics(data);
  const [sendStatus, setSendStatus] = useState('idle'); // idle | sending | sent | error
  const [sendError, setSendError] = useState(null);
  const [sendErrorKind, setSendErrorKind] = useState(null); // 'validation' | 'system'
  // Set only by the value-based date check, which is the one failure that names
  // a specific field. Kept separate from `sendError` because that holds raw
  // Salesforce text, which should not be shown to an advisor.
  const [sendFieldIssue, setSendFieldIssue] = useState(null);
  // Keys of the fields Submit found empty, so they can be marked in the form.
  const [missingKeys, setMissingKeys] = useState(EMPTY_KEYS);
  const formRef = useRef(null);

  const planOrder = data.planOrder || {};
  const prefills = getSmartPrefills(data, m);

  const valueFor = field => {
    const pair = retirementPairFor(field.key);
    if (pair) {
      const r = resolveRetirement({
        date: planOrder[pair.key],
        age: planOrder[pair.ageKey],
        pq: data?.employment?.[pair.pqPerson],
      });
      return field.key === pair.key ? r.date : r.age;
    }
    const stored = planOrder[field.key];
    if (stored !== undefined) return stored;
    return prefills[field.key] ?? '';
  };

  const setField = (key, val) => {
    update(`planOrder.${key}`, val);
    // Age and date answer the same retirement question, so filling one empties
    // the other. Without this the questionnaire's date would sit beside a typed
    // age and quietly win.
    const pair = retirementPairFor(key);
    if (pair && String(val ?? '').trim() !== '') {
      update(`planOrder.${key === pair.key ? pair.ageKey : pair.key}`, '');
    }
    // Clear any answer this change makes meaningless. Switching Current Cash
    // Flow from Surplus to Deficit greys out "Where is Excess Cash Going?", and
    // a greyed field holding a stale answer would still be submitted — planning
    // would receive "excess cash going: Roth" on a deficit household. Reads the
    // new value directly rather than through valueFor, which is still one
    // render behind at this point.
    const after = f => (f.key === key ? val : valueFor(f));
    planSections
      .flatMap(sec => sec.fields)
      .filter(f => f.onlyWhen && f.onlyWhen.field === key)
      .forEach(f => {
        if (!conditionApplies(f, after) && String(planOrder[f.key] ?? '').trim() !== '') {
          update(`planOrder.${f.key}`, '');
        }
      });
    // Drop the highlight the moment the advisor answers it, so the form stops
    // complaining about a field they have just fixed.
    // A retirement answer given in the age box still clears the highlight,
    // which sits on the question's date key.
    const markKey = pair ? pair.key : key;
    if (missingKeys.has(markKey) && String(val ?? '').trim() !== '') {
      setMissingKeys(prev => {
        const next = new Set(prev);
        next.delete(markKey);
        return next.size ? next : EMPTY_KEYS;
      });
    }
  };

  // `submitting` runs the completeness check first; Save Progress skips it, so
  // an advisor can always park a part-filled form.
  const handleSend = async ({ submitting = false } = {}) => {
    // Clear any previous field-specific date message; the checks below re-set it
    // if it still applies, so a fixed date stops showing a stale complaint.
    setSendFieldIssue(null);
    setSendError(null);

    if (submitting) {
      const missing = getMissingSubmitFields(valueFor, data);
      if (missing.length > 0) {
        setSendStatus('error');
        setSendErrorKind('validation');
        // Naming the fields in the banner was not enough on its own: with 20+
        // fields across four sections an advisor still had to hunt for them
        // ("it didn't highlight which fields were missing, so I had to hunt for
        // them and figure it out by trial and error" — Rachel Fuss,
        // 2026-08-10). Mark them in the form and scroll to the first one.
        setMissingKeys(new Set(missing.map(f => f.key)));
        setSendFieldIssue(
          `⚠ ${missing.length} field${missing.length === 1 ? '' : 's'} still to complete before submitting: ` +
          missing.map(f => `"${f.label}"`).join(', ') +
          `. They are highlighted below. Fill them in, or use Save Progress to come back to it later.`
        );
        const first = document.getElementById(`plan-${missing[0].key}`);
        if (first) {
          first.scrollIntoView({ behavior: 'smooth', block: 'center' });
          first.focus({ preventScroll: true });
        }
        return;
      }
      setMissingKeys(EMPTY_KEYS);
    }

    // Native validity catches calendar-impossible dates (Feb 30, or Feb 29 in
    // a non-leap year). A date input reports those as badInput with an empty
    // value, so our value-based check below can't see them. It also enforces
    // each field's min/max range. reportValidity() points the user at the
    // offending field with a native message.
    const form = formRef.current;
    if (form && !form.checkValidity()) {
      form.reportValidity();
      setSendStatus('error');
      setSendErrorKind('validation');
      // Name the field in the banner too. reportValidity() alone puts a native
      // tooltip on the control, which is easy to miss if it is off-screen or the
      // advisor clicks away. This path matters most for a date input in a
      // badInput state (a partly typed or calendar-impossible date): it reports
      // an EMPTY value while failing validation, so the field looks blank and
      // correct and the send just refuses. Read the label from the DOM rather
      // than the schema so this keeps working for any control type.
      const bad = form.querySelector('input:invalid, select:invalid, textarea:invalid');
      const labelEl = bad?.id ? form.querySelector(`label[for="${CSS.escape(bad.id)}"]`) : null;
      const label = labelEl?.textContent?.trim() || bad?.getAttribute('aria-label');
      if (label) {
        setSendFieldIssue(
          `⚠ "${label}" is not a valid entry. Clear the field and re-enter it, ` +
          `then click Retry. (A partly typed or impossible date can look empty ` +
          `while still being rejected.)`
        );
      }
      return;
    }

    // Backstop: value-based range check (covers our custom range messaging).
    // `find`, not `some`: two of the three date fields (client/spouse retirement)
    // are PREFILLED from the PQ rather than typed here, so the offending value
    // can be one the advisor never entered, on a row below the fold. Knowing a
    // date is bad but not which one is the difference between a ten-second fix
    // and an afternoon of guessing, so name it.
    const badDateField = planSections
      .flatMap(s => s.fields)
      .filter(f => f.type === 'date' || f.type === 'retirement')
      .find(f => {
        const { min, max } = getDateRange(f.dateRange);
        return isDateOutOfRange(valueFor(f), min, max);
      });
    if (badDateField) {
      setSendStatus('error');
      setSendErrorKind('validation');
      setSendFieldIssue(
        `⚠ "${badDateField.label}" is not a valid date. ` +
        `${getDateRangeHint(badDateField.dateRange)} Fix it, then click Retry.`
      );
      return;
    }

    // The eMoney gate below only engages when a field is flagged `emoney: true`
    // (currently none — see EmoneyGate). While dormant, `emoneyActive` is false
    // and Send behaves exactly as it did before the gate: straight to
    // sendPlanOrder, no name-coupled pre-push.
    const emoneyActive = getMissingEmoneyFields(data).length > 0 || getEmoneyRequirements().length > 0;

    // Backstop for the disabled button: nothing sends while an eMoney-required
    // PQ field (see the left-rail panel) is still blank. Off for the cutover.
    if (EMONEY_GATE_ENABLED && getMissingEmoneyFields(data).length > 0) {
      setSendStatus('error');
      setSendErrorKind('validation');
      return;
    }

    setSendStatus('sending');
    setSendError(null);
    setSendErrorKind(null);

    // Push the PQ before sending: the eMoney-required fields entered on this
    // screen live in the PQ store, and planning reads them off the Financial
    // Profile record — the plan order alone doesn't carry them. If an autosave
    // is already on the wire, wait it out and push again so the send is never
    // racing a half-synced profile. Skipped while the gate is dormant (nothing
    // is entered in a hidden panel to push).
    if (emoneyActive) {
      let pqRes = await saveNow();
      for (let i = 0; i < 10 && pqRes?.reason === 'in-flight'; i++) {
        await new Promise(r => setTimeout(r, 500));
        pqRes = await saveNow();
      }
      // 'read-only' means a sibling tab owns the PQ write lock — its autosave is
      // responsible for the profile, and blocking the send here would break a
      // flow that predates this gate. Proceed.
      if (!pqRes?.ok && pqRes?.reason !== 'read-only') {
        setSendStatus('error');
        setSendError(pqRes?.error || 'Could not save the questionnaire');
        setSendErrorKind(pqRes?.reason === 'validation' ? 'validation' : 'system');
        return;
      }
    }

    const result = await sendPlanOrder();
    if (result.ok) {
      setSendStatus('sent');
      // A successful SUBMIT (not Save Progress) is the workflow's "advisor is
      // done" moment: freeze the PQ as the handoff snapshot. Planning measures
      // its changes against this baseline, and "View as handed off" renders
      // it. Submitting again re-freezes — a re-submit is a new handoff. NOT in
      // planning mode: a planner touching the POF is not the advisor finishing,
      // and re-freezing there would erase the baseline their own edits are
      // measured against.
      // ARCHIVE_ENABLED: the snapshot lands in Salesforce's pqSnapshot, which
      // does not exist in production yet. Without the field the write fails
      // silently, so the archive is held to the environments that can store
      // it. Remove this gate once the field is created (Jimmy, 2026-08-19).
      if (submitting && !planningMode && ARCHIVE_ENABLED) archivePq();
      setTimeout(() => setSendStatus('idle'), 2000);
    } else {
      // Keep the error visible until the advisor retries (no auto-clear).
      setSendStatus('error');
      setSendError(result.error || 'Failed to send');
      setSendErrorKind(classifySaveError(result.status));
    }
  };

  const saveLabel =
    sendStatus === 'sending' ? 'Saving…' :
    sendStatus === 'sent' ? 'Saved ✓' :
    sendStatus === 'error' ? 'Retry' :
    'Save Progress';

  return (
    <section
      className="screen is-active"
      style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}
    >
      <div className="plan-nav no-print">
        <div className="plan-nav-brand brand" aria-label="Pure Financial Advisors — Plan Order">
          <span className="brand-text">Pure Financial Advisors</span>
          <span className="brand-divider" aria-hidden="true"></span>
          <span className="brand-context">Plan Order</span>
        </div>
        <div className="plan-nav-actions">
          {/* Save Progress always works, even on a part-filled form: parking
              work half-done is a normal thing to do mid-appointment. Submit is
              the one that checks completeness. Neither exists in view mode,
              where the Plan Order is being read rather than filled. */}
          {readOnly ? (
            <span className="pill">View only</span>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-ghost-dark"
                onClick={() => handleSend()}
                disabled={sendStatus === 'sending'}
              >
                {saveLabel}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleSend({ submitting: true })}
                disabled={sendStatus === 'sending'}
              >
                Submit
              </button>
            </>
          )}
        </div>
      </div>

      {sendStatus === 'sent' && (
        <div className="save-success-banner no-print">
          ✓ Plan Order saved.
        </div>
      )}

      {sendStatus === 'error' && (
        <div className="save-error-banner no-print">
          {sendFieldIssue
            ? sendFieldIssue
            : sendErrorKind === 'validation' ? SEND_MSG_VALIDATION : SEND_MSG_SYSTEM}
          {/* Show the underlying failure verbatim. Without this the advisor sees
              a generic "check your fields" for every failure, including ones no
              field change can fix, and support has nothing to go on unless
              someone can open DevTools.

              Label it neutrally: this text is NOT always from Salesforce. The
              send first pushes the PQ, so a PQ-side validation ("Please enter
              the client's first name before saving") surfaces here too. Calling
              it "from Salesforce" sent one investigation hunting in the wrong
              system entirely. */}
          {sendError && !sendFieldIssue && (
            <div className="save-error-detail">
              <strong>Details:</strong> {String(sendError).slice(0, 600)}
            </div>
          )}
        </div>
      )}

      <div className="plan-layout">
        <aside className="plan-summary">
          {EMONEY_GATE_ENABLED && <EmoneyGate />}
          <PlanSummaryPanel data={data} />
        </aside>
        <div className="plan-form-wrap">
          <form ref={formRef} autoComplete="off" onSubmit={e => e.preventDefault()}>
            <fieldset className="form-lock" disabled={readOnly}>
              {planSections.map(section => (
                <div key={section.id} className={`plan-form-card plan-card-${section.color}`}>
                  <div className="plan-card-header">{section.title}</div>
                  <div className="plan-card-body">
                    {section.fields.map(field => (
                      <PlanField
                        key={field.key}
                        field={field}
                        value={valueFor(field)}
                        ageValue={field.ageKey ? valueFor({ key: field.ageKey }) : undefined}
                        dob={field.dobKey ? data?.family?.[field.dobKey] : undefined}
                        missing={missingKeys.has(field.key)}
                        disabled={!conditionApplies(field, valueFor)}
                        onChange={val => setField(field.key, val)}
                        onAgeChange={field.ageKey ? val => setField(field.ageKey, val) : undefined}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </fieldset>
          </form>
        </div>
      </div>
    </section>
  );
}
